import { entityConstants, scenarioConstants } from '../constants';


function updateScenarioInEntities(list, targetEntityId, updateEntityFn) {
  if (!Array.isArray(list)) return list;

  const targetId = Number(targetEntityId);
  let changed = false;

  const next = list.map(ent => {
    let updated = ent;

    // Recurse first
    if (Array.isArray(ent.children) && ent.children.length) {
      const nextChildren = updateScenarioInEntities(ent.children, targetId, updateEntityFn);
      if (nextChildren !== ent.children) {
        updated = { ...updated, children: nextChildren };
        changed = true;
      }
    }

    // Then update this node if it matches
    if (Number(ent.entity_id) === targetId) {
      const candidate = updateEntityFn(updated);
      if (candidate !== updated) {
        updated = candidate;
        changed = true;
      }
    }

    return updated;
  });

  // preserve reference when nothing changed anywhere
  return changed ? next : list;
}

const applyOrder = (list, orderedIds, idKey) => {
  const pos = new Map(orderedIds.map((id, i) => [Number(id), i]));
  return [...(list || [])].sort((a, b) => {
    const ai = pos.has(Number(a[idKey])) ? pos.get(Number(a[idKey])) : Infinity;
    const bi = pos.has(Number(b[idKey])) ? pos.get(Number(b[idKey])) : Infinity;
    return ai - bi;
  })
  .map((n, i) => ({ ...n, sort_order: i }));
};

const reorderChildEntities = (nodes, parent, orderedIds) => {
  if (parent == null) return applyOrder(nodes, orderedIds, 'entity_id');
  return (nodes || []).map((n) =>
    Number(n.entity_id) === Number(parent)
      ? { ...n, children: applyOrder(n.children, orderedIds, 'entity_id') } 
      : { ...n, children: reorderChildEntities(n.children, parent, orderedIds) },
  );
};
 
const initialState = {
    fetchingEntities: false,
    entities: null,
    fetchingEntitiesError: null,
     version: 0,

    creatingEntity: false,
    creatingEntitiesError: null,

    updatingEntity: false,
    updatingEntitiesError: null,

    deletingEntity: false,
    deletingEntitiesError: null,
};

export function entities(state = initialState, action) {
    switch (action.type) {
        case entityConstants.GET_ENTITY_REQUEST: {
            return {
                ...state,
                fetchingEntities: true,
                entities: null,
                fetchingEntitiesError: null,
            };
        }
        case entityConstants.GET_ENTITY_SUCCESS: {
            return {
                ...state,
                fetchingEntities: false,
                entities: action.entities,
                fetchingEntitiesError: null,
            };
        }
        case entityConstants.GET_ENTITY_FAILURE: {
            return {
                ...state,
                fetchingEntities: false,
                fetchingEntitiesError: action.error,
            };
        }

        case entityConstants.CREATE_ENTITY_REQUEST: {
            return {
                ...state,
                creatingEntity: true,
                creatingEntitiesError: null,
            };
        }
        case entityConstants.CREATE_ENTITY_SUCCESS: {
            return {
                ...state,
                creatingEntity: false,
                entities: action.entities,
            };
        }
        case entityConstants.CREATE_ENTITY_FAILURE: {
            return {
                ...state,
                creatingEntity: false,
                creatingEntitiesError: action.error,
            };
        }

        case entityConstants.UPDATE_ENTITY_REQUEST: {
            return {
                ...state,
                updatingEntity: true,
                updatingEntitiesError: null,
            };
        }
        case entityConstants.UPDATE_ENTITY_SUCCESS: {
            return {
                ...state,
                updatingEntity: false,
                entities: action.entities,
                version: (state.version ?? 0) + 1,
            };
        }
        case entityConstants.UPDATE_ENTITY_FAILURE: {
            return {
                ...state,
                updatingEntity: false,
                updatingEntitiesError: action.error,
            };
        }

        case entityConstants.DELETE_ENTITY_REQUEST: {
            return {
                ...state,
                deletingEntity: true,
                deletingEntitiesError: null,
            };
        }
        case entityConstants.DELETE_ENTITY_SUCCESS: {
            return {
                ...state,
                deletingEntity: false,
                entities: action.entities,
                version: (state.version ?? 0) + 1,
            };
        }
        case entityConstants.DELETE_ENTITY_FAILURE: {
            return {
                ...state,
                deletingEntitiesError: action.error,
            };
        }

        case entityConstants.REORDER_ENTITIES_REQUEST: {
            const { parent, orderedIds } = action;
            if (!orderedIds?.length) return state;

            return {
                ...state,
                entities: reorderChildEntities(state.entities ?? [], parent, orderedIds),
                // no version bump — reorder is not a structural change
            };
            }

            case entityConstants.REORDER_ENTITIES_SUCCESS:
                 return state;   // optimistic update already applied
            
            case entityConstants.REORDER_ENTITIES_FAILURE:
                return action.entities ? { ...state, entities: action.entities } : state;

         
            case scenarioConstants.CREATE_SCENARIO_SUCCESS: {
                const s = action.parsedScenario ?? action.scenario ?? action.payload ?? action;

                // If the action carries the already-updated entity tree (from createScenario thunk),
                // use it directly — insertion was already done before dispatch
                if (action.entities) {
                    return {
                        ...state,
                        entities:      action.entities,
                        version:       (state.version ?? 0) + 1,
                        creatingEntity: false,
                    };
                }

    // Fallback: reducer does the insertion itself (for any other callers)
    const scenario = {
        scenario_id:       s.scenario_id,
        entity_id:         s.entity_id,
        name:              s.name,
        key:               `s:${s.scenario_id}`, // deterministic key, not backend-supplied
        scenario_currency: s.scenario_currency,
        group_currency:    s.group_currency,
    };

    const next = updateScenarioInEntities(state.entities ?? [], s.entity_id, (ent) => ({
        ...ent,
        scenario: [...(ent.scenario ?? []), scenario],
    }));

    return {
        ...state,
        entities:      next,
        version:       (state.version ?? 0) + 1,
        creatingEntity: false,
    };
}
    case scenarioConstants.DELETE_SCENARIO_SUCCESS: {
      const entityId =
        action.entityId ?? action.payload?.entityId ?? action.parsedScenario?.entity_id;
      const scenarioId =
        action.scenarioId ?? action.payload?.scenarioId ?? action.scenario_id;

      if (entityId == null || scenarioId == null) return state;

      const next = updateScenarioInEntities(state.entities ?? [], entityId, (ent) => ({
        ...ent,
        scenario: (ent.scenario ?? []).filter(sc => Number(sc.scenario_id) !== Number(scenarioId)),
      }));

      return {
        ...state,                                 // keep other flags
        entities: next,
        version: (state.version ?? 0) + 1,        // bump
        deletingEntity: false,
      };
    }

    case scenarioConstants.UPDATE_SCENARIO_SUCCESS: {
       if (!action.entities) return state;
        return {
            ...state,
            entities: action.entities,
            // no version bump — rename is not a structural change
        };
    }
        case scenarioConstants.REORDER_SCENARIOS_REQUEST: {
            const { entityId, orderedIds } = action;
           
            if (entityId == null || !orderedIds?.length) return state;

            const next = updateScenarioInEntities(state.entities ?? [], entityId, (ent) => ({
                ...ent,
                scenario: applyOrder(ent.scenario ?? [], orderedIds, 'scenario_id'),
            }));

            return { ...state, entities: next }; // no version bump — reorder is not structural
        }

        case scenarioConstants.REORDER_SCENARIOS_SUCCESS:
            return state;   // optimistic update already applied
            

        case scenarioConstants.REORDER_SCENARIOS_FAILURE: {
           return action.entities ? { ...state, entities: action.entities } : state;
        }
    
    default: {
        return state;
    }
}
}
